<template>
  <div>
    <v-btn
        class="login-text"
        variant="text"
        :ripple="false"
        @click="login"
        v-if="!isLogin"
    >
      {{ $t("로그인") }}
    </v-btn>
    <v-menu
        v-else-if="isLogin"
        v-model="menu"
        location="bottom end"
        transition="none"
        :close-on-content-click="true"
    >
      <template v-slot:activator="{ props }">
        <v-btn
            v-bind="props"
            icon
            class="btn-user-name"
            :aria-label="`${$session.getUserInfo()?.NAME} ${$t('프로필')}`"
            :ripple="false"
        >
          {{ state.userInfo.NAME }}
        </v-btn>
      </template>
      <v-sheet class="menu-list" role="menu">
        <div
            v-for="(btn, index) in btnList"
            :key="btn.key"
            ref="btnRefs"
            class="menu-list-item"
            @click="onClickHandler(btn.key)"
            @keydown="onBtnItemKeydown($event, index, btn.key)"
            tabindex="1"
            :aria-label="$t(btn.label)"
            role="menuitem"
            aria-roledescription="button"
        >
          <span class="menu-list-item-label">{{ $t(btn.label) }}</span>
        </div>
      </v-sheet>
    </v-menu>
  </div>
</template>

<script setup lang="ts">
import { $session } from "framework"
import { nextTick, onBeforeUpdate, computed, reactive, ref, watch } from "vue"
import { useSiteStore } from "@/store/site"
import { useAuthStore } from "@/store/auth"
import router from "@/router"

interface UserInfo {
  NAME: string;
  USER_ID: string;
  USER_NO: string;
  USER_ROLES: {
    dotSaasVision: string;
    dotSaasClass: string;
    dotSaasSupport: string;
  };
}

interface State {
  userInfo: UserInfo;
}

interface Store {
  param: {
    a: string;
    r: string;
    u: string;
  } | null;
}

const siteStore: Store = useSiteStore()
const authStore = useAuthStore()

const isLogin = computed(() => authStore.isAuthenticated)

const state = reactive<State>({
  userInfo: {
    NAME: "",
    USER_ID: "",
    USER_NO: "",
    USER_ROLES: {
      dotSaasVision: "",
      dotSaasClass: "",
      dotSaasSupport: "",
    },
  },
});
const initUserInfo = () => {
  const userName = $session.getUserInfo()?.NAME?.slice(0, 1) || "U";
  const userId = $session.getUserInfo()?.USER_ID;
  const userNo = $session.getUserNo();
  const userRole = $session.getUserRoles("dot-saas-support");

  state.userInfo.NAME = userName;
  state.userInfo.USER_NO = userNo;
  state.userInfo.USER_ID = userId;
  state.userInfo.USER_ROLES.dotSaasSupport = userRole;
};

if (isLogin.value) {
  initUserInfo();
}

watch(isLogin, (val) => {
  if (val) {
    initUserInfo();
  } else {
    state.userInfo = {
      NAME: "",
      USER_ID: "",
      USER_NO: "",
      USER_ROLES: {
        dotSaasVision: "",
        dotSaasClass: "",
        dotSaasSupport: "",
      },
    };
  }
});

const menu = ref(false)
const btnList = [
  {
    key: "profile",
    label: "프로필"
  },
  {
    key: "logout",
    label: "로그아웃"
  }
]
const btnRefs = ref<HTMLElement[]>([])

watch(menu, async (isOpen) => {
  if (!isOpen) return

  await nextTick()
  requestAnimationFrame(() => {
    const el = btnRefs.value[0]
    if (el instanceof HTMLElement) {
      el.focus()
    }
  })
})

const login = () => {
  router.push("/login")
}

const profile = () => {
  const sso = $session.getSsoDomain();
  const s = $session.getSiteNo();
  const c = $session.getCompNo();
  const a = siteStore.param?.a;
  const cb = window.location.origin;

  const url = `${sso}/basic?s=${s}&c=${c}&a=${a}&cb=${cb}/login-callback`;

  window.open(url)
}

const logOut = async () => {
  await $session.signOut();

  authStore.setAuthenticated(false);

  state.userInfo = {
    NAME: "",
    USER_ID: "",
    USER_NO: "",
    USER_ROLES: {
      dotSaasVision: "",
      dotSaasClass: "",
      dotSaasSupport: "",
    },
  };
};

const onBtnItemKeydown = (
    event: KeyboardEvent,
    index: number,
    code: string
) => {
  const key = event.key

  console.log("on btn item keydown evet", index, code)

  if (key === "ArrowDown" || key === "ArrowUp") {
    event.preventDefault()

    const lastIndex = btnList.length - 1
    let nextIndex = index

    if (key === "ArrowDown") {
      nextIndex = index === lastIndex ? 0 : index + 1
    }
    else if (key === "ArrowUp") {
      nextIndex = index === 0 ? lastIndex : index - 1
    }
    requestAnimationFrame(() => {
      const el = btnRefs.value[nextIndex]
      if (el instanceof HTMLElement) {
        el.focus()
      }
    })
    return
  }

  if (key === "Enter" || key === " " || key === "Spacebar") {
    event.preventDefault()
    event.stopPropagation()
    onClickHandler(code)
  }
}

const onClickHandler = (key: string) => {
  switch (key) {
    case "profile":
      profile()
      break
    case "logout":
      logOut()
      break
  }
}

onBeforeUpdate(() => {
  btnRefs.value = []
})
</script>

<style scoped lang="scss">
.login-text {
  font-size: 16px;
  text-transform: none;
  color: #212529;
}

.btn-user-name {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  font-size: 32px;
  font-weight: bold;
  color: #fff;
  background: #aaabab;
  text-transform: initial;
  text-transform: none;
}

.menu-list {
  background: #aaabab !important;
  width: 160px;
  margin-top: 10px;
  padding: 6px 0;
  border-radius: 6px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);

  &-item {
    font-size: 20px;
    font-weight: 500;
    color: #fff;
    padding: 4px 16px;
    letter-spacing: 0;
    width: 100%;
    justify-content: flex-start;
    cursor: pointer;

    &:hover {
      background: #cd4a12;
    }
    &-label {
      flex-grow: 1;
      white-space: nowrap;
    }
  }
}
</style>